import * as React from "react";
import {
  type CurrencyContextValue,
  type CurrencyProviderProps,
  type CurrencyPreferences,
  type Money,
  type ConvertedMoney,
  type ConvertedDisplay,
  type ExchangeRatesResponse,
  CURRENCY_SYMBOLS,
} from "./types";

const STALE_THRESHOLD = 24 * 60 * 60 * 1000;
const DEFAULT_RATES_ENDPOINT = "/v1/config/exchange-rates";

export const CurrencyContext = React.createContext<CurrencyContextValue | null>(
  null,
);

CurrencyContext.displayName = "CurrencyContext";

export function CurrencyProvider({
  children,
  defaultCurrency = "USD",
  showOriginal = true,
  onCurrencyChange,
  fetchRates,
  enabled = true,
}: CurrencyProviderProps) {
  const [exchangeRates, setExchangeRates] = React.useState<
    Record<string, number>
  >({});
  const [baseCurrency, setBaseCurrency] = React.useState<string>("USD");
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const [isStale, setIsStale] = React.useState<boolean>(false);
  const [lastUpdated, setLastUpdated] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const [preferences, setPreferences] = React.useState<CurrencyPreferences>({
    displayCurrency: defaultCurrency,
    showOriginalCurrency: showOriginal,
  });

  const loadRates = React.useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      let response: ExchangeRatesResponse;

      if (fetchRates) {
        response = await fetchRates();
      } else {
        const res = await fetch(DEFAULT_RATES_ENDPOINT);
        if (!res.ok) {
          throw new Error(`Failed to fetch exchange rates: ${res.statusText}`);
        }
        const json = await res.json();
        response = json.data;
      }

      setExchangeRates(response.rates);
      setBaseCurrency(response.base);
      setLastUpdated(response.timestamp);
      setIsStale(response.isStale ?? false);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unknown error";
      setError(message);
      console.error("Failed to load exchange rates:", err);
    } finally {
      setIsLoading(false);
    }
  }, [fetchRates]);

  React.useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      return;
    }

    const schedule =
      typeof requestIdleCallback === "function"
        ? requestIdleCallback
        : (cb: () => void) => setTimeout(cb, 0);

    const id = schedule(() => {
      loadRates();
    });

    const interval = setInterval(loadRates, 60 * 60 * 1000);
    return () => {
      if (typeof cancelIdleCallback === "function") {
        cancelIdleCallback(id as number);
      } else {
        clearTimeout(id as ReturnType<typeof setTimeout>);
      }
      clearInterval(interval);
    };
  }, [enabled, loadRates]);

  const setDisplayCurrency = React.useCallback(
    (currency: string) => {
      setPreferences((prev) => ({ ...prev, displayCurrency: currency }));
      onCurrencyChange?.(currency);
    },
    [onCurrencyChange],
  );

  const setShowOriginalCurrency = React.useCallback((show: boolean) => {
    setPreferences((prev) => ({ ...prev, showOriginalCurrency: show }));
  }, []);

  const getRate = React.useCallback(
    (fromCurrency: string, toCurrency: string): number | null => {
      if (fromCurrency === toCurrency) return 1;
      if (!exchangeRates[fromCurrency] || !exchangeRates[toCurrency]) {
        return null;
      }
      return exchangeRates[toCurrency] / exchangeRates[fromCurrency];
    },
    [exchangeRates],
  );

  const convert = React.useCallback(
    (
      money: Money | null | undefined,
      targetCurrency?: string,
    ): ConvertedMoney | null => {
      if (!money) return null;

      const target = targetCurrency ?? preferences.displayCurrency;

      if (money.currency === target) {
        return {
          original: money,
          converted: money,
          rate: 1,
          rateTimestamp: lastUpdated ?? new Date().toISOString(),
          isStale: false,
        };
      }

      const rate = getRate(money.currency, target);
      if (rate === null) return null;

      const stale =
        isStale ||
        (lastUpdated
          ? Date.now() - new Date(lastUpdated).getTime() > STALE_THRESHOLD
          : true);

      return {
        original: money,
        converted: { currency: target, amount: money.amount * rate },
        rate,
        rateTimestamp: lastUpdated ?? new Date().toISOString(),
        isStale: stale,
      };
    },
    [preferences.displayCurrency, getRate, lastUpdated, isStale],
  );

  const formatMoney = React.useCallback(
    (money: Money | null | undefined): string => {
      if (!money) return "-";
      try {
        return new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: money.currency,
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        }).format(money.amount);
      } catch {
        const symbol = CURRENCY_SYMBOLS[money.currency] ?? money.currency;
        return `${symbol} ${money.amount.toLocaleString("en-US", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        })}`;
      }
    },
    [],
  );

  const formatConverted = React.useCallback(
    (money: Money | null | undefined): string => {
      if (!money) return "-";
      const converted = convert(money);
      if (!converted) return formatMoney(money);
      return formatMoney(converted.converted);
    },
    [convert, formatMoney],
  );

  const formatWithOriginal = React.useCallback(
    (money: Money | null | undefined): ConvertedDisplay | null => {
      if (!money) return null;

      const converted = convert(money);
      if (!converted) {
        return {
          primary: formatMoney(money),
          secondary: null,
          tooltip: {
            originalAmount: formatMoney(money),
            convertedAmount: formatMoney(money),
            rate: "1",
            rateDate: new Date().toISOString(),
            isStale: false,
          },
        };
      }

      const isSameCurrency = money.currency === preferences.displayCurrency;
      const rateDate = converted.rateTimestamp
        ? new Date(converted.rateTimestamp).toLocaleDateString()
        : "Unknown";

      return {
        primary: formatMoney(converted.converted),
        secondary: isSameCurrency ? null : `(${formatMoney(money)})`,
        tooltip: {
          originalAmount: formatMoney(money),
          convertedAmount: formatMoney(converted.converted),
          rate: `1 ${money.currency} = ${converted.rate.toFixed(4)} ${preferences.displayCurrency}`,
          rateDate,
          isStale: converted.isStale,
        },
      };
    },
    [convert, formatMoney, preferences.displayCurrency],
  );

  const refreshRates = React.useCallback(async () => {
    await loadRates();
  }, [loadRates]);

  const value: CurrencyContextValue = React.useMemo(
    () => ({
      displayCurrency: preferences.displayCurrency,
      baseCurrency,
      exchangeRates,
      isLoading,
      isStale,
      lastUpdated,
      error,
      preferences,
      setDisplayCurrency,
      setShowOriginalCurrency,
      convert,
      formatMoney,
      formatConverted,
      formatWithOriginal,
      getRate,
      refreshRates,
    }),
    [
      preferences,
      baseCurrency,
      exchangeRates,
      isLoading,
      isStale,
      lastUpdated,
      error,
      setDisplayCurrency,
      setShowOriginalCurrency,
      convert,
      formatMoney,
      formatConverted,
      formatWithOriginal,
      getRate,
      refreshRates,
    ],
  );

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
}
